export type MonitorStatsWorkflow = {
  id: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt?: number | null;
  runDailyStats?: ReadonlyArray<{
    day: number;
    runCount: number | null;
    lastRunAt?: number | null;
  }>;
};

export type WorkflowExecutionVolumeRow = {
  workflowId: string;
  spaceId: string;
  name: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  executionCount: number;
  lastRunAt: number | null;
};

export type SpaceExecutionVolumeRow = {
  spaceId: string;
  executionCount: number;
  workflowCount: number;
};

export function computeWorkflowExecutionVolumeRows(
  workflows: ReadonlyArray<MonitorStatsWorkflow>
): WorkflowExecutionVolumeRow[] {
  const rows: WorkflowExecutionVolumeRow[] = [];

  for (const workflow of workflows) {
    const stats = workflow.runDailyStats ?? [];
    if (stats.length === 0) continue;

    let executionCount = 0;
    let lastRunAt = 0;
    for (const stat of stats) {
      executionCount += stat.runCount ?? 0;
      lastRunAt = Math.max(lastRunAt, stat.lastRunAt ?? stat.day);
    }

    rows.push({
      workflowId: workflow.id,
      spaceId: workflow.spaceId,
      name: workflow.name,
      badgeSeed: workflow.badgeSeed,
      pausedAt: workflow.pausedAt ?? null,
      executionCount,
      lastRunAt,
    });
  }

  return rows.sort(
    (a, b) =>
      b.executionCount - a.executionCount || a.name.localeCompare(b.name)
  );
}

export function computeSpaceExecutionVolumeRows(
  workflowRows: readonly WorkflowExecutionVolumeRow[],
  teamSpaceIds: readonly string[]
): SpaceExecutionVolumeRow[] {
  const bySpace = new Map<
    string,
    { executionCount: number; workflowCount: number }
  >();
  for (const spaceId of teamSpaceIds) {
    bySpace.set(spaceId, { executionCount: 0, workflowCount: 0 });
  }

  for (const row of workflowRows) {
    const entry = bySpace.get(row.spaceId);
    if (!entry) continue;
    entry.executionCount += row.executionCount;
    entry.workflowCount += 1;
  }

  const rows: SpaceExecutionVolumeRow[] = [];
  for (const [spaceId, data] of bySpace) {
    rows.push({ spaceId, ...data });
  }

  return rows
    .filter((row) => row.executionCount > 0)
    .sort(
      (a, b) =>
        b.executionCount - a.executionCount ||
        a.spaceId.localeCompare(b.spaceId)
    );
}

export function formatExecutionVolume(count: number): string {
  return count.toLocaleString("en-US");
}

// Bars are scaled logarithmically so spaces spanning several orders of magnitude
// stay visible and comparable.
export function executionVolumeBarFraction(count: number, max: number): number {
  if (count <= 0 || max <= 0) return 0;
  if (max === 1) return 1;
  return Math.log1p(count) / Math.log1p(max);
}

export function workflowRowsForSpace(
  workflowRows: readonly WorkflowExecutionVolumeRow[],
  spaceId: string
): WorkflowExecutionVolumeRow[] {
  return workflowRows
    .filter((row) => row.spaceId === spaceId)
    .sort(
      (a, b) =>
        b.executionCount - a.executionCount || a.name.localeCompare(b.name)
    );
}
